import { assert } from './assert.js';
import {
  META_CLIENT_ID,
  type AggregateRecord,
  type DedupRecord,
  type MetadataRecord,
  type NewEvent,
  type StateRecord,
  type StoredEvent,
} from './storeTypes.js';

// One IndexedDB database per app. Written over the platform API rather than a wrapper: a unit
// of work issues every request on one transaction and needs the request lifecycle in hand. The
// factory is injectable so tests give every device its own storage.

const STORE_AGGREGATES = 'aggregates';
const STORE_EVENTS = 'events';
const STORE_STATES = 'states';
const STORE_METADATA = 'metadata';
const STORE_DEDUP = 'requestDedup';

const INDEX_AGGREGATES_TYPE = 'by_type';
const INDEX_EVENTS_AGG_SI_LI = 'by_agg_si_li';

// Version 2 stores that version 3 drops. Named here because the upgrade reads them first.
const V2_STORE_SNAPSHOTS = 'snapshots';
const V2_STORE_OUTBOX = 'outbox';

export const DB_VERSION = 3;
const UNIT_OF_WORK_STORES = [STORE_AGGREGATES, STORE_EVENTS, STORE_STATES, STORE_DEDUP] as const;
const EVENTS_PER_APPEND_MAX = 1_000_000;

export interface StoreOptions {
  appName: string;
  generateClientId?: (() => string) | undefined;
  factory?: IDBFactory | undefined;
}

// Every method issues its requests on the unit's transaction. The body that receives one is
// synchronous between awaits of these methods: awaiting anything else lets the transaction
// commit underneath it.
export interface UnitOfWork {
  getRecord(): Promise<AggregateRecord | undefined>;
  putRecord(record: AggregateRecord): Promise<void>;
  getState(): Promise<StateRecord | undefined>;
  putState(state: StateRecord): Promise<void>;
  // Log rows with li above `afterLi`, in li order.
  readTail(afterLi: number): Promise<StoredEvent[]>;
  readUnsent(): Promise<StoredEvent[]>;
  // Appends at li = firstLi, firstLi + 1, ... and returns the stored rows. The caller allocates
  // firstLi from the record it read in this same transaction.
  append(events: ReadonlyArray<NewEvent>, firstLi: number): Promise<StoredEvent[]>;
  // Stamps the server version on sent rows. A row already stamped with the same version is left
  // alone: two tabs can drain the same rows and both acks arrive.
  stampSi(lis: ReadonlyArray<number>, si: number): Promise<void>;
  // Renumbers unsent rows to sit strictly above `floorSeq`, preserving order; returns the new
  // high-water (floorSeq when nothing moved).
  renumberUnsent(floorSeq: number): Promise<number>;
  // Deletes rows with 0 < si < keepFrom; returns how many.
  trim(keepFrom: number): Promise<number>;
  // Deletes every row, the record and the state.
  purge(): Promise<void>;
  // The online engine's request-dedup index, event_id to the version its write produced. Lives
  // in the same transaction as the cursor advance so an entry never names a version the local
  // record has not seen, and never outlives a purge.
  getDedup(eventId: string): Promise<DedupRecord | undefined>;
  putDedup(record: DedupRecord): Promise<void>;
  // Deletes this aggregate's entries with expiresAt <= before; returns how many.
  pruneDedup(before: number): Promise<number>;
}

export class Store {
  private readonly appName: string;
  private readonly generateClientId: () => string;
  private readonly factory: IDBFactory;
  private db: IDBDatabase | undefined;
  private closed = false;
  private clientIdInflight: Promise<string> | undefined;

  constructor(options: StoreOptions) {
    if (options.appName.length === 0) throw new Error('Store: appName must not be empty');
    if (!/^[A-Za-z0-9_-]+$/.test(options.appName)) {
      throw new Error('Store: appName must match /^[A-Za-z0-9_-]+$/');
    }
    this.appName = options.appName;
    this.generateClientId = options.generateClientId ?? defaultClientIdGenerator;
    const factory = options.factory ?? (globalThis as { indexedDB?: IDBFactory }).indexedDB;
    if (factory === undefined) throw new Error('Store: no IndexedDB factory available');
    this.factory = factory;
  }

  async open(): Promise<void> {
    if (this.closed) throw new Error('Store: cannot open after close()');
    if (this.db !== undefined) return;
    this.db = await openDatabase(this.factory, `celeriant:${this.appName}`);
  }

  close(): void {
    this.db?.close();
    this.db = undefined;
    this.closed = true;
  }

  private requireDb(): IDBDatabase {
    if (this.closed) throw new Error('Store: closed');
    if (this.db === undefined) throw new Error('Store: open() must be called before any operation');
    return this.db;
  }

  // The one readwrite path for aggregate data. `body` runs inside the transaction; a throw
  // aborts it and nothing lands.
  async unitOfWork<T>(type: string, id: string, mode: IDBTransactionMode, body: (uow: UnitOfWork) => Promise<T>): Promise<T> {
    assert(type.length > 0, 'unitOfWork: type is set');
    assert(id.length > 0, 'unitOfWork: id is set');
    const db = this.requireDb();
    const tx = db.transaction(UNIT_OF_WORK_STORES, mode);
    const done = transactionDone(tx);
    let result: T;
    try {
      result = await body(new Unit(tx, type, id));
    } catch (e: unknown) {
      abortQuietly(tx);
      await done.catch(() => undefined);
      throw e;
    }
    await done;
    return result;
  }

  getRecord(type: string, id: string): Promise<AggregateRecord | undefined> {
    return this.unitOfWork(type, id, 'readonly', (uow) => uow.getRecord());
  }

  getState(type: string, id: string): Promise<StateRecord | undefined> {
    return this.unitOfWork(type, id, 'readonly', (uow) => uow.getState());
  }

  async listRecords(type: string): Promise<AggregateRecord[]> {
    const tx = this.requireDb().transaction(STORE_AGGREGATES, 'readonly');
    const rows = await request(tx.objectStore(STORE_AGGREGATES).index(INDEX_AGGREGATES_TYPE).getAll(type)) as AggregateRecord[];
    await transactionDone(tx);
    return rows;
  }

  async countUnsent(type: string, id: string): Promise<number> {
    const tx = this.requireDb().transaction(STORE_EVENTS, 'readonly');
    const count = await request(tx.objectStore(STORE_EVENTS).index(INDEX_EVENTS_AGG_SI_LI).count(unsentRange(type, id)));
    await transactionDone(tx);
    return count;
  }

  // Single-flight: the promise is stored before the first await, so N concurrent first uses share
  // one generation. Across tabs `add` refuses the second writer and the loser adopts the
  // persisted winner.
  getOrCreateClientId(): Promise<string> {
    if (this.clientIdInflight === undefined) {
      this.clientIdInflight = this.resolveClientId().catch((e: unknown) => {
        this.clientIdInflight = undefined;
        throw e;
      });
    }
    return this.clientIdInflight;
  }

  private async resolveClientId(): Promise<string> {
    const db = this.requireDb();
    const existing = await this.readClientId(db);
    if (existing !== undefined) return existing;
    const next = this.generateClientId();
    assert(next.length > 0, 'resolveClientId: the generator produced an id');
    const tx = db.transaction(STORE_METADATA, 'readwrite');
    const done = transactionDone(tx);
    const record: MetadataRecord = { key: META_CLIENT_ID, value: next };
    const won = await addOrSkip(tx.objectStore(STORE_METADATA), record);
    await done;
    if (won !== undefined) return next;
    const winner = await this.readClientId(db);
    assert(winner !== undefined, 'resolveClientId: a losing add means a winner is persisted');
    return winner;
  }

  private async readClientId(db: IDBDatabase): Promise<string | undefined> {
    const tx = db.transaction(STORE_METADATA, 'readonly');
    const row = await request(tx.objectStore(STORE_METADATA).get(META_CLIENT_ID)) as MetadataRecord | undefined;
    await transactionDone(tx);
    if (row === undefined) return undefined;
    return typeof row.value === 'string' && row.value.length > 0 ? row.value : undefined;
  }
}

class Unit implements UnitOfWork {
  constructor(
    private readonly tx: IDBTransaction,
    private readonly type: string,
    private readonly id: string,
  ) {}

  async getRecord(): Promise<AggregateRecord | undefined> {
    return await request(this.tx.objectStore(STORE_AGGREGATES).get([this.type, this.id])) as AggregateRecord | undefined;
  }

  async putRecord(record: AggregateRecord): Promise<void> {
    assert(record.type === this.type && record.id === this.id, 'putRecord: record belongs to this unit');
    assert(record.nextClientSeq >= record.lastSentClientSeq, 'putRecord: nothing sent beyond what was stamped');
    await request(this.tx.objectStore(STORE_AGGREGATES).put(record));
  }

  async getState(): Promise<StateRecord | undefined> {
    return await request(this.tx.objectStore(STORE_STATES).get([this.type, this.id])) as StateRecord | undefined;
  }

  async putState(state: StateRecord): Promise<void> {
    assert(state.type === this.type && state.id === this.id, 'putState: state belongs to this unit');
    await request(this.tx.objectStore(STORE_STATES).put(state));
  }

  async readTail(afterLi: number): Promise<StoredEvent[]> {
    assert(afterLi >= 0, 'readTail: afterLi is non-negative');
    const range = IDBKeyRange.bound([this.type, this.id, afterLi], [this.type, this.id, Number.POSITIVE_INFINITY], true, false);
    const rows = await request(this.tx.objectStore(STORE_EVENTS).getAll(range)) as StoredEvent[];
    assert(rows.every((r) => r.li > afterLi), 'readTail: every row is above afterLi');
    return rows;
  }

  async readUnsent(): Promise<StoredEvent[]> {
    const rows = await request(this.tx.objectStore(STORE_EVENTS).index(INDEX_EVENTS_AGG_SI_LI).getAll(unsentRange(this.type, this.id))) as StoredEvent[];
    assert(rows.every((r) => r.si === 0), 'readUnsent: every row is unsent');
    return rows;
  }

  async append(events: ReadonlyArray<NewEvent>, firstLi: number): Promise<StoredEvent[]> {
    assert(events.length <= EVENTS_PER_APPEND_MAX, `append: ${events.length} events exceeds ${EVENTS_PER_APPEND_MAX}`);
    assert(firstLi >= 1, 'append: li starts at 1');
    const store = this.tx.objectStore(STORE_EVENTS);
    const rows: StoredEvent[] = [];
    const pending: Promise<unknown>[] = [];
    for (let i = 0; i < events.length; i += 1) {
      const event = events[i]!;
      assert(event.type === this.type && event.id === this.id, 'append: event belongs to this unit');
      assert(event.clientSeq > 0, 'append: clientSeq is allocated');
      const row: StoredEvent = { ...event, li: firstLi + i };
      rows.push(row);
      pending.push(request(store.add(row)));
    }
    await Promise.all(pending);
    assert(rows.length === events.length, 'append: one row per event');
    return rows;
  }

  async stampSi(lis: ReadonlyArray<number>, si: number): Promise<void> {
    assert(si > 0, 'stampSi: si is a server version');
    const store = this.tx.objectStore(STORE_EVENTS);
    const rows = await Promise.all(lis.map((li) => request(store.get([this.type, this.id, li])) as Promise<StoredEvent | undefined>));
    const puts: Promise<unknown>[] = [];
    for (const row of rows) {
      assert(row !== undefined, 'stampSi: every li names a row');
      if (row.si === si) continue;
      assert(row.si === 0, 'stampSi: a row carries one server version');
      puts.push(request(store.put({ ...row, si })));
    }
    assert(puts.length <= rows.length, 'stampSi: at most one write per row');
    await Promise.all(puts);
  }

  async renumberUnsent(floorSeq: number): Promise<number> {
    assert(floorSeq >= 0, 'renumberUnsent: floor is non-negative');
    const rows = await this.readUnsent();
    if (rows.length === 0 || rows[0]!.clientSeq > floorSeq) return floorSeq;
    for (let i = 1; i < rows.length; i += 1) assert(rows[i - 1]!.clientSeq < rows[i]!.clientSeq, 'renumberUnsent: unsent rows ascend in clientSeq');
    const store = this.tx.objectStore(STORE_EVENTS);
    const shift = floorSeq + 1 - rows[0]!.clientSeq;
    assert(shift > 0, 'renumberUnsent: rows move up');
    await Promise.all(rows.map((row) => request(store.put({ ...row, clientSeq: row.clientSeq + shift }))));
    return rows[rows.length - 1]!.clientSeq + shift;
  }

  async trim(keepFrom: number): Promise<number> {
    assert(keepFrom >= 1, 'trim: keepFrom is a version');
    if (keepFrom === 1) return 0;
    const range = IDBKeyRange.bound([this.type, this.id, 1, 0], [this.type, this.id, keepFrom, 0], false, true);
    const deleted = await deleteRange(this.tx.objectStore(STORE_EVENTS).index(INDEX_EVENTS_AGG_SI_LI), range);
    assert(deleted >= 0, 'trim: a count');
    return deleted;
  }

  async purge(): Promise<void> {
    const range = IDBKeyRange.bound([this.type, this.id, 0], [this.type, this.id, Number.POSITIVE_INFINITY]);
    await request(this.tx.objectStore(STORE_EVENTS).delete(range));
    await request(this.tx.objectStore(STORE_DEDUP).delete(dedupRange(this.type, this.id)));
    await request(this.tx.objectStore(STORE_AGGREGATES).delete([this.type, this.id]));
    await request(this.tx.objectStore(STORE_STATES).delete([this.type, this.id]));
  }

  async getDedup(eventId: string): Promise<DedupRecord | undefined> {
    assert(eventId.length > 0, 'getDedup: eventId is set');
    return await request(this.tx.objectStore(STORE_DEDUP).get([this.type, this.id, eventId])) as DedupRecord | undefined;
  }

  async putDedup(record: DedupRecord): Promise<void> {
    assert(record.type === this.type && record.id === this.id, 'putDedup: record belongs to this unit');
    assert(record.version >= 1, 'putDedup: a dedup entry names a landed version');
    await request(this.tx.objectStore(STORE_DEDUP).put(record));
  }

  async pruneDedup(before: number): Promise<number> {
    assert(before >= 0, 'pruneDedup: the horizon is a wall clock');
    const store = this.tx.objectStore(STORE_DEDUP);
    return new Promise((resolve, reject) => {
      let deleted = 0;
      const req = store.openCursor(dedupRange(this.type, this.id));
      req.onerror = () => reject(req.error ?? new Error('IndexedDB cursor failed'));
      req.onsuccess = () => {
        const cursor = req.result;
        if (cursor === null) { resolve(deleted); return; }
        if ((cursor.value as DedupRecord).expiresAt <= before) { deleted += 1; cursor.delete(); }
        cursor.continue();
      };
    });
  }
}

function dedupRange(type: string, id: string): IDBKeyRange {
  return IDBKeyRange.bound([type, id, ''], [type, id, '\uffff']);
}

function unsentRange(type: string, id: string): IDBKeyRange {
  return IDBKeyRange.bound([type, id, 0, 0], [type, id, 0, Number.POSITIVE_INFINITY]);
}

function request<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
  });
}

// `add` that resolves undefined when the key exists and leaves the transaction alive. The error
// event must be cancelled, or IndexedDB aborts the whole transaction on the request's behalf.
function addOrSkip(store: IDBObjectStore, value: unknown): Promise<IDBValidKey | undefined> {
  return new Promise((resolve, reject) => {
    const req = store.add(value);
    req.onsuccess = () => resolve(req.result);
    req.onerror = (event) => {
      if (req.error?.name === 'ConstraintError') {
        event.preventDefault();
        event.stopPropagation();
        resolve(undefined);
        return;
      }
      reject(req.error ?? new Error('IndexedDB add failed'));
    };
  });
}

function deleteRange(source: IDBIndex, range: IDBKeyRange): Promise<number> {
  return new Promise((resolve, reject) => {
    let deleted = 0;
    const req = source.openCursor(range);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB cursor failed'));
    req.onsuccess = () => {
      const cursor = req.result;
      if (cursor === null) { resolve(deleted); return; }
      deleted += 1;
      cursor.delete();
      cursor.continue();
    };
  });
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
  });
}

function abortQuietly(tx: IDBTransaction): void {
  try { tx.abort(); } catch { /* already finished */ }
}

function openDatabase(factory: IDBFactory, name: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = factory.open(name, DB_VERSION);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    req.onblocked = () => reject(new Error(`Store: open of ${name} blocked by another connection`));
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (event) => {
      const db = req.result;
      const tx = req.transaction;
      assert(tx !== null, 'onupgradeneeded: a versionchange transaction exists');
      upgrade(db, tx, event.oldVersion).catch((e: unknown) => {
        abortQuietly(tx);
        reject(e);
      });
    };
  });
}

// Version 0 is a fresh install. Versions 1 and 2 are the previous tree's layout; IndexedDB
// keyPaths are immutable, so the aggregate stores are dropped and recreated, and what they held
// is carried into the new log first.
async function upgrade(db: IDBDatabase, tx: IDBTransaction, oldVersion: number): Promise<void> {
  assert(oldVersion >= 0 && oldVersion < DB_VERSION, `upgrade: from version ${oldVersion}`);
  const carried = oldVersion === 0 ? undefined : await readV2(db, tx);
  for (const name of [STORE_AGGREGATES, STORE_EVENTS, STORE_STATES, V2_STORE_SNAPSHOTS, V2_STORE_OUTBOX]) {
    if (db.objectStoreNames.contains(name)) db.deleteObjectStore(name);
  }
  const aggregates = db.createObjectStore(STORE_AGGREGATES, { keyPath: ['type', 'id'] });
  aggregates.createIndex(INDEX_AGGREGATES_TYPE, 'type');
  const events = db.createObjectStore(STORE_EVENTS, { keyPath: ['type', 'id', 'li'] });
  events.createIndex(INDEX_EVENTS_AGG_SI_LI, ['type', 'id', 'si', 'li']);
  db.createObjectStore(STORE_STATES, { keyPath: ['type', 'id'] });
  if (!db.objectStoreNames.contains(STORE_METADATA)) db.createObjectStore(STORE_METADATA, { keyPath: 'key' });
  if (!db.objectStoreNames.contains(STORE_DEDUP)) db.createObjectStore(STORE_DEDUP, { keyPath: ['type', 'id', 'eventId'] });
  if (carried !== undefined) await writeCarried(tx, carried);
}

// The previous tree's rows, as it stored them.
interface V2Event {
  type: string; id: string; serverVersion: number; clientSeq: number;
  eventTypeMajor: number; eventTypeMinor: number; eventTimestamp: number;
  clientId: string; userId?: string; eventValue: string;
}
interface V2Record {
  type: string; id: string; schemaVersion: number; serverVersion: number;
  lastSentClientSeq: number; nextClientSeq: number; createdAt: number; modifiedAt: number;
}
interface V2Snapshot { type: string; id: string; schemaVersion: number; snapshotAtServerVersion: number; state: unknown }

interface Carried {
  records: V2Record[];
  events: V2Event[];
  snapshots: V2Snapshot[];
}

async function readV2(db: IDBDatabase, tx: IDBTransaction): Promise<Carried> {
  const all = async <T>(name: string): Promise<T[]> =>
    (db.objectStoreNames.contains(name) ? await request(tx.objectStore(name).getAll()) as T[] : []);
  const [records, events, snapshots] = await Promise.all([all<V2Record>(STORE_AGGREGATES), all<V2Event>(STORE_EVENTS), all<V2Snapshot>(V2_STORE_SNAPSHOTS)]);
  return { records, events, snapshots };
}

// Server events first in version order, then the unsent mirrors in clientSeq order, so the new
// log's li order is the order the old tree folded them in. The old snapshot becomes the state
// document at the li of the last event it covers, which keeps a trimmed history foldable; the
// record's appliedLi matches it so open() folds the rows above as a tail. A history whose
// lowest row sits above version 1 was trimmed, and trimFloor says so, so a later schema bump
// without `migrate` refuses to replay the stub.
async function writeCarried(tx: IDBTransaction, carried: Carried): Promise<void> {
  const events = tx.objectStore(STORE_EVENTS);
  const byAggregate = new Map<string, V2Event[]>();
  for (const e of carried.events) {
    const key = `${e.type}\0${e.id}`;
    const list = byAggregate.get(key);
    if (list === undefined) byAggregate.set(key, [e]);
    else list.push(e);
  }
  interface Placement { liAtVersion: Map<number, number>; lastLi: number; lowestSi: number }
  const placements = new Map<string, Placement>();
  for (const [key, list] of byAggregate) {
    list.sort((a, b) => {
      const av = a.serverVersion === 0 ? Number.POSITIVE_INFINITY : a.serverVersion;
      const bv = b.serverVersion === 0 ? Number.POSITIVE_INFINITY : b.serverVersion;
      if (av !== bv) return av - bv;
      return a.clientSeq - b.clientSeq;
    });
    const placement: Placement = { liAtVersion: new Map(), lastLi: 0, lowestSi: 0 };
    for (const e of list) {
      placement.lastLi += 1;
      const row: StoredEvent = {
        type: e.type, id: e.id, li: placement.lastLi, si: e.serverVersion, clientSeq: e.clientSeq, clientId: e.clientId,
        userId: e.userId, ts: e.eventTimestamp, serverTs: 0,
        typeMajor: e.eventTypeMajor, typeMinor: e.eventTypeMinor, value: e.eventValue,
      };
      await request(events.add(row));
      if (e.serverVersion > 0) {
        placement.liAtVersion.set(e.serverVersion, placement.lastLi);
        if (placement.lowestSi === 0 || e.serverVersion < placement.lowestSi) placement.lowestSi = e.serverVersion;
      }
    }
    placements.set(key, placement);
  }
  const states = tx.objectStore(STORE_STATES);
  const appliedLiByAggregate = new Map<string, number>();
  for (const s of carried.snapshots) {
    const key = `${s.type}\0${s.id}`;
    const placement = placements.get(key);
    let appliedLi = 0;
    if (placement !== undefined) {
      for (const [version, li] of placement.liAtVersion) if (version <= s.snapshotAtServerVersion && li > appliedLi) appliedLi = li;
    }
    appliedLiByAggregate.set(key, appliedLi);
    const state: StateRecord = { type: s.type, id: s.id, schemaVersion: s.schemaVersion, appliedLi, state: s.state };
    await request(states.put(state));
  }
  const aggregates = tx.objectStore(STORE_AGGREGATES);
  for (const r of carried.records) {
    const key = `${r.type}\0${r.id}`;
    const placement = placements.get(key);
    const lowestSi = placement?.lowestSi ?? 0;
    const trimFloor = lowestSi > 0 ? lowestSi : (r.serverVersion > 0 ? r.serverVersion + 1 : 1);
    assert(trimFloor >= 1, 'writeCarried: trimFloor is a version');
    const record: AggregateRecord = {
      type: r.type, id: r.id, schemaVersion: r.schemaVersion, serverVersion: r.serverVersion,
      lastSentClientSeq: r.lastSentClientSeq, nextClientSeq: Math.max(r.nextClientSeq, r.lastSentClientSeq),
      lastLi: placement?.lastLi ?? 0,
      appliedLi: appliedLiByAggregate.get(key) ?? 0,
      trimFloor,
      createdAt: r.createdAt, modifiedAt: r.modifiedAt,
    };
    assert(record.appliedLi <= record.lastLi, 'writeCarried: the state document never leads the log');
    await request(aggregates.put(record));
  }
}

// 22-char URL-safe base64 of 128 random bits. Persisted on first use; a storage wipe means a new id.
function defaultClientIdGenerator(): string {
  const bytes = new Uint8Array(16);
  if (typeof crypto === 'undefined' || typeof crypto.getRandomValues !== 'function') {
    throw new Error('Store: crypto.getRandomValues required for default clientId generator');
  }
  crypto.getRandomValues(bytes);
  let bin = '';
  for (const b of bytes) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
